import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import {
  Clock,
  CheckCircle,
  TrendingUp,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { analyticsAPI } from "../services/analyticsApi";
import "./DashboardKeyMetrics.css";

interface OverviewResponse {
  success: boolean;
  data: {
    positionsFilled: number;
    avgTimeToFill: number | null;
    hiredCount: number;
    openJobs: number;
    previousPeriod?: {
      positionsFilled: number;
      avgTimeToFill: number | null;
      hiredCount: number;
    };
  };
}

interface TimeMetricsResponse {
  success: boolean;
  data: {
    timeToFill: {
      avgDays: number | null;
      minDays: number | null;
      maxDays: number | null;
      jobsFilled: number;
      previousPeriod?: {
        avgDays: number | null;
        jobsFilled: number;
      };
    };
  };
}

interface MetricItem {
  icon: React.ReactNode;
  label: string;
  value: string;
  trend: "up" | "down" | "neutral";
  trendPositive: boolean;
}

const MetricSkeleton: React.FC = () => (
  <div className="db-metrics__skeleton">
    {[1, 2, 3].map((i) => (
      <div key={i} className="db-metrics__skeleton-item">
        <div className="db-metrics__skeleton-icon" />
        <div className="db-metrics__skeleton-text">
          <div className="db-metrics__skeleton-value" />
          <div className="db-metrics__skeleton-label" />
        </div>
      </div>
    ))}
  </div>
);

const DashboardKeyMetrics: React.FC = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const {
    data: timeData,
    isLoading: timeLoading,
    error: timeError,
  } = useQuery<TimeMetricsResponse>(
    ["dashboard-time-metrics"],
    () => analyticsAPI.getTimeMetrics({ range: "30d" }),
    { staleTime: 300000 },
  );

  const {
    data: overviewData,
    isLoading: overviewLoading,
    error: overviewError,
  } = useQuery<OverviewResponse>(
    ["dashboard-overview"],
    () => analyticsAPI.getOverview({ range: "30d" }),
    { staleTime: 300000 },
  );

  const isLoading = timeLoading || overviewLoading;
  const hasError = timeError || overviewError;

  if (isLoading) {
    return (
      <div className="dashboard-section-full db-metrics">
        <div className="dashboard-section-header">
          <h3>{t("dashboard.keyMetrics", "Key Metrics")}</h3>
        </div>
        <MetricSkeleton />
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="dashboard-section-full db-metrics">
        <div className="dashboard-section-header">
          <h3>{t("dashboard.keyMetrics", "Key Metrics")}</h3>
        </div>
        <div className="db-metrics__error">
          <span>{t("errors.loadingFailed", "Failed to load metrics")}</span>
          <button
            className="view-all-link"
            onClick={() => navigate("/analytics")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
          >
            {t("actions.viewAll")} →
          </button>
        </div>
      </div>
    );
  }

  const avgDays = timeData?.data?.timeToFill?.avgDays;
  const prevAvgDays = timeData?.data?.timeToFill?.previousPeriod?.avgDays;
  const positionsFilled = overviewData?.data?.positionsFilled ?? 0;
  const hiredCount = overviewData?.data?.hiredCount ?? 0;
  const prev = overviewData?.data?.previousPeriod;

  // Compare current vs previous period. For time-to-fill, lower is better (inverted).
  const compareTrend = (
    current: number,
    previous: number | null | undefined,
    lowerIsBetter = false,
  ): { trend: "up" | "down" | "neutral"; trendPositive: boolean } => {
    if (previous === null || previous === undefined)
      return { trend: "neutral", trendPositive: false };
    if (current === previous) return { trend: "neutral", trendPositive: false };
    const isUp = current > previous;
    return {
      trend: isUp ? "up" : "down",
      trendPositive: lowerIsBetter ? !isUp : isUp,
    };
  };

  const ttfTrend =
    avgDays !== null && avgDays !== undefined
      ? compareTrend(avgDays, prevAvgDays, true)
      : { trend: "neutral" as const, trendPositive: false };

  const metrics: MetricItem[] = [
    {
      icon: <Clock size={20} />,
      label: t("dashboard.avgTimeToFill", "Avg. Time to Fill"),
      value:
        avgDays !== null && avgDays !== undefined
          ? `${Math.round(avgDays)} days`
          : "--",
      ...ttfTrend,
    },
    {
      icon: <CheckCircle size={20} />,
      label: t("dashboard.positionsFilled", "Positions Filled (30d)"),
      value: String(positionsFilled),
      ...compareTrend(positionsFilled, prev?.positionsFilled),
    },
    {
      icon: <TrendingUp size={20} />,
      label: t("dashboard.hired", "Hired (30d)"),
      value: String(hiredCount),
      ...compareTrend(hiredCount, prev?.hiredCount),
    },
  ];

  return (
    <div className="dashboard-section-full db-metrics">
      <div className="dashboard-section-header">
        <h3>{t("dashboard.keyMetrics", "Key Metrics")}</h3>
        <button
          className="view-all-link"
          onClick={() => navigate("/analytics")}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
          }}
        >
          {t("actions.viewAll")} →
        </button>
      </div>

      <div className="db-metrics__list">
        {metrics.map((metric) => (
          <div key={metric.label} className="db-metrics__item">
            <div className="db-metrics__item-icon">{metric.icon}</div>
            <div className="db-metrics__item-content">
              <div className="db-metrics__item-value">{metric.value}</div>
              <div className="db-metrics__item-label">{metric.label}</div>
            </div>
            {metric.trend !== "neutral" && (
              <div
                className={`db-metrics__item-trend ${metric.trendPositive ? "db-metrics__item-trend--positive" : "db-metrics__item-trend--negative"}`}
              >
                {metric.trend === "up" ? (
                  <ArrowUp size={16} />
                ) : (
                  <ArrowDown size={16} />
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardKeyMetrics;
